import {
  App,
  Button,
  Card,
  Col,
  Input,
  Pagination,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Select,
  message,
  Popconfirm,
} from "antd";
import { EyeFilled, LoadingOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import CulsightPageLoader from "../../components/CulsightPageLoader";
import { ANNOUNCEMENT_LIST, ANNOUNCEMENT_STATUS, DELETE_ANNOUNCEMENT } from "../../apis/apis";
import { formatToIST } from "../../../helper/CommonHelper";

function Announcements() {
  const { notification } = App.useApp();
  const navigate = useNavigate();

  const [page_size, set_page_size] = useState(10);
  const [loader, setLoader] = useState(true);
  const [pagination_loader, set_pagination_loader] = useState(false);
  const [table_data, set_table_data] = useState([]);
  const [current_page, set_current_page] = useState(1);
  const [total_pages, set_total_pages] = useState(0);
  const [total_announcements, set_total_announcements] = useState(0);
  const [placeholder, set_placeholder] = useState("Search by Name");
  const [search_query_key, set_search_query_key] = useState("title");
  const [search_query_value, set_search_query_value] = useState("");

  const fetchResults = useCallback(
    debounce(async (key, value) => {
      try {
        set_pagination_loader(true);

        const FD = new FormData();
        FD.append("page", 1);
        FD.append("per_page", page_size);

        if (value !== "") {
          FD.append(key, value);
        }

        const API = await ANNOUNCEMENT_LIST(FD);

        if (API?.data?.status) {
          set_table_data(API.data.data);
          set_current_page(API.data.current_page);
          set_total_pages(API.data.total_pages);
          set_total_announcements(API.data.total_announcements);
        }
      } catch (e) {
        console.error(e);
      } finally {
        set_pagination_loader(false);
      }
    }, 500),
    [page_size]
  );

  const LIST_API = async () => {
    const FORM_DATA = new FormData();
    FORM_DATA.append("page", current_page);
    FORM_DATA.append("per_page", page_size);

    const API_CALL = await ANNOUNCEMENT_LIST(FORM_DATA);

    if (API_CALL?.data?.status) {
      set_table_data(API_CALL.data.data);
      set_current_page(API_CALL.data.current_page);
      set_total_pages(API_CALL.data.total_pages);
      set_total_announcements(API_CALL.data.total_news);

    }

    setLoader(false);
  };

  useEffect(() => {
    LIST_API();
  }, []);

  const change_status = async (id) => {
    try {
      setLoader(true);

      const FORM_DATA = new FormData();
      FORM_DATA.append("id", id);

      const response = await ANNOUNCEMENT_STATUS(FORM_DATA);

      if (response?.data?.status) {
        notification.success({
          message: "Successful",
          description: response?.data?.message,
        });
        LIST_API();
      }
    } catch (e) {
      message.error("Status update failed!");
    } finally {
      setLoader(false);
    }
  };

  const deleteAnnouncement = async (id) => {
    try {
      setLoader(true);

      const FD = new FormData();
      FD.append("id", id);

      const API = await DELETE_ANNOUNCEMENT(FD);

      if (API?.data?.status) {
        notification.success({
          message: "Deleted",
          description: API?.data?.message,
        });
        LIST_API();
      }
    } catch (e) {
      notification.error({
        message: "Failed",
        description: "Deletion failed!",
      });
    } finally {
      setLoader(false);
    }
  };

  const handleInput = (e) => {
    const value = e.target.value;
    set_search_query_value(value);
    fetchResults(search_query_key, value);
  };

  const pagination_on_change = async (page, size) => {
    set_pagination_loader(true);

    const FORM_DATA = new FormData();
    FORM_DATA.append("page", page);
    FORM_DATA.append("per_page", size);

    if (search_query_value !== "") {
      FORM_DATA.append(search_query_key, search_query_value);
    }

    const API_CALL = await ANNOUNCEMENT_LIST(FORM_DATA);

    if (API_CALL?.data?.status) {
      set_table_data(API_CALL?.data?.data);
      set_current_page(API_CALL?.data?.current_page);
      set_total_pages(API_CALL?.data?.total_pages);
      set_total_announcements(API_CALL?.data?.total_news);

    }

    set_pagination_loader(false);
  };

  const columns = [
    {
      title: "Created On",
      dataIndex: "created_at",
      render: (date) => <span>{formatToIST(date)}</span>,
    },
    {
      title: "Name",
      dataIndex: "title",
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (active) =>
        active ? <Tag color="#1fa7a1" style={{fontSize:"14px",padding:"4px 9px"}}>Active</Tag> 
      : <Tag color="error" style={{fontSize:"14px",padding:"4px 9px"}}>Inactive</Tag>,
    },
    {
      title: "Action",
      render: (_, row) => (
        <Space size="middle">
          <Tooltip title="Edit Announcement">
            <Button
              type="primary"
              size="small"
              onClick={() => navigate(`/edit-announcements/${row.id}`)}

            >
              <EyeFilled />
            </Button>
          </Tooltip>

          <Popconfirm
            title="Are you sure you want to delete?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => deleteAnnouncement(row.id)}
          >
            <Button danger size="small" type="primary">
              <DeleteOutlined />
            </Button>
          </Popconfirm>

          <Popconfirm
            title="Do you want to change the status?"
            onConfirm={() => change_status(row?.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Change Status">
              <Button
                size="small"
                style={{
                  backgroundColor: row.status ? "#52c41a" : "#ff4d4f",
                  borderColor: row.status ? "#52c41a" : "#ff4d4f",
                  color: "#fff"
                }}
                icon={
                  row.status ? (
                    <CheckOutlined />
                  ) : (
                    <CloseOutlined />
                  )
                }
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const selectBefore = (
    <Select
      defaultValue="Name"
      style={{ width: 120 }}
      onChange={(value) => {
        if (value === "Name") {
          set_search_query_key("title");
          set_placeholder("Search by Name");
          fetchResults("title", search_query_value);
        }
      }}
    >
      <Select.Option value="Name">Name</Select.Option>
    </Select>
  );


  return (
    <div className="lms-body">
      <Card>
        <Row>
          <Col span={12}>
            <h2>Announcements</h2>
          </Col>

          <Col span={12}>
            <div style={{ float: "right" }}>
              <Button
                type="primary"
                style={{ marginRight: "10px" }}
                onClick={() => navigate("/add-announcements")}
              >
                Add Announcement
              </Button>
            </div>
          </Col>
        </Row>

        <Row>
          <Col span={12}>
            <Input
              addonBefore={<span>Name</span>}
              placeholder={placeholder}
              onChange={handleInput}
              size="large"
            />
          </Col>
        </Row>

        {loader ? (
          <CulsightPageLoader />
        ) : pagination_loader ? (
          <div style={{ textAlign: "center", padding: "60px" }}>
            <Spin indicator={<LoadingOutlined spin />} size="large" />
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={table_data}
              pagination={false}
              rowKey="id"
              style={{ marginTop: "15px" }}
            />

            {total_pages > 0 ? (
              <div style={{ float: "right", marginTop: "20px" }}>
                <Pagination
                  current={current_page}
                  total={total_announcements}
                  pageSize={page_size}
                  showSizeChanger
                  pageSizeOptions={["10", "20", "50", "100"]}
                  onChange={(page) => pagination_on_change(page, page_size)}
                  onShowSizeChange={(page, size) => {
                    set_page_size(size);
                    pagination_on_change(1, size);
                  }}
                  className="no-search-pagination"
                />

                <style>
                  {`
    .no-search-pagination .ant-select-selection-search-input {
      display: none !important;
    }
  `}
                </style>

              </div>
            ) : (
              <h2 style={{ textAlign: "center", color: "red" }}>

              </h2>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default Announcements;
